import React, { useState } from 'react';
import { CircularProgress, MenuItem, TextField, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Languages } from 'common/types';
import { useTranslation } from 'services/localization';
import { getLanguageName } from 'common/locales/utils';
import { RENTLE_FREE_LANGUAGES } from 'common/locales/utils/constants';

import * as NotificationActions from 'actions/NotificationActions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { api } from 'common/frontend/api';
import { useHasFeature } from 'hooks/useHasFeature';
import { uniq } from 'lodash';

const DefaultLanguage = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { t } = useTranslation();
	const { hasFeature } = useHasFeature();
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const activeLanguages = useSelector(ShopSelectors.activeShopLanguages);
	const [selectedDefaultLanguage, setDefaultLanguage] = useState<Languages>(
		shopPublicInfo?.defaultLanguage ?? 'en',
	);
	const [isUpdating, setIsUpdating] = useState<boolean>(false);
	const hasLanguages = hasFeature('LANGUAGES', { scope: 'shop' });

	const possibleLanguages = hasLanguages
		? uniq([...activeLanguages, ...RENTLE_FREE_LANGUAGES])
		: RENTLE_FREE_LANGUAGES;

	const handleDefaultLanguageChange = async (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		if (!shopPublicInfo) return;
		try {
			const language = e.target.value as Languages;
			setDefaultLanguage(language);
			setIsUpdating(true);
			await api()
				.shops.doc(shopPublicInfo.shopId)
				.public.update({
					defaultLanguage: language,
					languages: hasLanguages ? uniq([...shopPublicInfo.languages, language]) : [language],
				});
			dispatch(
				NotificationActions.showNotification({
					variant: 'info',
					message: t('account.languages.languageSettings.mainLanguageNotification', {
						lang: getLanguageName(language, t),
						defaultValue: 'Your main language is now {{lang}}',
					}),
				}),
			);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t(
						'account.languages.languageSettings.mainLanguageError',
						'Unable to update language ',
					),
					variant: 'error',
				}),
			);
		}
		setIsUpdating(false);
	};

	return (
		<TextField
			select
			fullWidth
			value={selectedDefaultLanguage}
			onChange={handleDefaultLanguageChange}
			disabled={isUpdating}
			InputProps={{
				endAdornment: isUpdating ? (
					<CircularProgress size={20} sx={{ marginRight: theme.spacing(4) }} />
				) : null,
			}}
		>
			{possibleLanguages.map((language, i) => (
				<MenuItem value={language} key={i}>
					{getLanguageName(language, t)}
				</MenuItem>
			))}
		</TextField>
	);
};

export default DefaultLanguage;
